/**
 * Mídia que ainda não chegou (media_pull em curso) — a política do lado do
 * cliente, PURA de propósito: sem React, sem alias `@/`, porque o web não tem
 * executor de testes e a prova mora na API (apps/api/src/shared/
 * chat-media-await.test.ts), como a guarda de navegação do Simplificado.
 *
 * A mensagem de áudio/imagem do WhatsApp nasce SEM a cópia no storage do ERP
 * (mediaUrl null) e a ganha segundos depois. Quem avisa a tela é o evento
 * chat.message.updated; quando ele não chega (SSE caído, aba em segundo
 * plano, evento perdido entre réplicas), nada mais perguntava de novo — o
 * polling incremental só traz mensagem NOVA, nunca campo de mensagem já vista,
 * e a bolha ficava em "baixando mídia…" até recarregar a página (28/09).
 *
 * A regra: enquanto houver mídia esperando a cópia, a conversa reconsulta a
 * cada MEDIA_AWAIT_POLL_MS — só a janela a partir dessa mensagem —, até o teto
 * de MEDIA_AWAIT_MAX_MS contado da CRIAÇÃO da mensagem. Passado o teto, a
 * bolha diz "Mídia indisponível" e oferece tentar de novo: um estado que dura
 * segundos precisa de quem o faça sair, e um que não vai sair precisa dizer
 * isso em vez de girar para sempre.
 */
import type { ChatContentType } from '@erp/shared';

/** reconsulta enquanto houver mídia esperando a cópia */
export const MEDIA_AWAIT_POLL_MS = 4_000;
/** teto da espera, contado da criação da mensagem — a cópia leva segundos */
export const MEDIA_AWAIT_MAX_MS = 2 * 60_000;

const MEDIA_TYPES: ReadonlySet<ChatContentType> = new Set([
  'image',
  'sticker',
  'audio',
  'video',
  'document',
]);

/** contentTypes que renderizam mídia (os demais renderizam como texto). */
export function isMediaContent(m: { contentType: ChatContentType }): boolean {
  return MEDIA_TYPES.has(m.contentType);
}

/** o mínimo para decidir — serve à mensagem do /chats e à entrada da linha do tempo */
export interface MediaAwaitInput {
  contentType: ChatContentType;
  mediaUrl: string | null;
  createdAt: Date | string;
}

/**
 * - `ready`: tem a cópia (ou não é mídia) — nada a esperar;
 * - `awaiting`: mídia sem cópia, dentro do teto — "baixando mídia…";
 * - `unavailable`: mídia sem cópia, teto vencido — "Mídia indisponível".
 */
export type MediaAwaitState = 'ready' | 'awaiting' | 'unavailable';

/** instante (ms) em que a espera da mensagem vence */
export function mediaAwaitDeadline(m: Pick<MediaAwaitInput, 'createdAt'>): number {
  return new Date(m.createdAt).getTime() + MEDIA_AWAIT_MAX_MS;
}

export function mediaAwaitState(m: MediaAwaitInput, now: number): MediaAwaitState {
  if (!isMediaContent(m) || m.mediaUrl !== null) return 'ready';
  return now < mediaAwaitDeadline(m) ? 'awaiting' : 'unavailable';
}

/**
 * Menor id entre as mensagens com mídia AINDA esperando a cópia (dentro do
 * teto) — o `after_id` da reconsulta é ele menos um, e a janela cobre só dali
 * para a frente. null = nada esperando: o polling volta ao normal.
 */
export function oldestAwaitedMediaId(
  messages: ReadonlyArray<MediaAwaitInput & { id: number }>,
  now: number
): number | null {
  let oldest: number | null = null;
  for (const m of messages) {
    if (mediaAwaitState(m, now) !== 'awaiting') continue;
    if (oldest === null || m.id < oldest) oldest = m.id;
  }
  return oldest;
}

/**
 * Funde uma resposta que REPETE mensagens já conhecidas (reconsulta completa
 * ou a janela da mídia) no cache: a conhecida é trocada pelo registro fresco
 * (tick, wamid, mediaUrl), a ordem do cache é preservada e as inéditas entram
 * no fim na ordem do servidor — nunca duplica.
 */
export function mergeMessagesById<T extends { id: number }>(prev: readonly T[], fresh: readonly T[]): T[] {
  const byId = new Map(fresh.map((m) => [m.id, m]));
  const known = new Set(prev.map((m) => m.id));
  return [...prev.map((m) => byId.get(m.id) ?? m), ...fresh.filter((m) => !known.has(m.id))];
}
